import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { settingsApi } from '../api/payment';
import { PageHeader } from '../components/ui/PageHeader';
import { SkeletonText } from '../components/ui/Skeleton';
import { VERSION_LABEL } from '../version';

export function AboutScreen() {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'about'],
    queryFn: settingsApi.getAbout,
    staleTime: 300_000,
    gcTime: 600_000,
  });

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader title="Sobre" showBack />

      <div className="flex-1 px-4 py-5 space-y-4">
        {/* App header */}
        <div className="bg-gradient-to-br from-brand-500 to-brand-700 rounded-3xl p-6 text-white flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
            <img src="/home-stock-transp-256x256.png" alt="HomeStock" className="w-10 h-10" />
          </div>
          <div>
            {isLoading ? (
              <div className="space-y-2">
                <div className="h-5 w-32 bg-white/30 rounded animate-pulse" />
                <div className="h-4 w-48 bg-white/20 rounded animate-pulse" />
              </div>
            ) : (
              <>
                <p className="text-xl font-bold">{data?.['app-name'] ?? 'HomeStock AI'}</p>
                <p className="text-brand-100 text-sm">{data?.['app-tagline'] ?? 'Controle doméstico inteligente'}</p>
              </>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <p className="text-sm font-bold text-gray-700 mb-2">Sobre o app</p>
          {isLoading ? (
            <SkeletonText lines={3} />
          ) : (
            <p className="text-sm text-gray-600 leading-relaxed">
              {data?.['about-description'] ??
                'HomeStock AI é um aplicativo de controle de estoque doméstico e lista de compras, projetado para simplificar as compras do dia a dia.'}
            </p>
          )}
        </div>

        {/* Contact */}
        {(isLoading || data?.['about-contact-email'] || data?.['about-whatsapp'] || data?.['support-url']) && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <p className="text-sm font-bold text-gray-700 px-4 pt-4 pb-2">Contato &amp; Suporte</p>
            {isLoading ? (
              <div className="px-4 pb-4 space-y-2">
                <SkeletonText lines={2} />
              </div>
            ) : (
              <>
                {data?.['about-contact-email'] && (
                  <a
                    href={`mailto:${data['about-contact-email']}`}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors touch-manipulation ${
                      data?.['about-whatsapp'] || data?.['support-url'] ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#2563eb" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">E-mail</p>
                      <p className="text-sm text-blue-600 font-medium">{data['about-contact-email']}</p>
                    </div>
                  </a>
                )}
                {data?.['about-whatsapp'] && (
                  <a
                    href={`https://wa.me/${data['about-whatsapp'].replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors touch-manipulation ${
                      data?.['support-url'] ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#16a34a">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">WhatsApp</p>
                      <p className="text-sm text-green-600 font-medium">{data['about-whatsapp']}</p>
                    </div>
                  </a>
                )}
                {data?.['support-url'] && (
                  <a
                    href={data['support-url']}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors touch-manipulation"
                  >
                    <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Central de ajuda</p>
                      <p className="text-sm text-brand-600 font-medium">Acessar suporte</p>
                    </div>
                  </a>
                )}
              </>
            )}
          </div>
        )}

        {/* Legal links */}
        {(isLoading || data?.['terms-url'] || data?.['privacy-url']) && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <p className="text-sm font-bold text-gray-700 px-4 pt-4 pb-2">Documentos Legais</p>
            {isLoading ? (
              <div className="px-4 pb-4">
                <SkeletonText lines={2} />
              </div>
            ) : (
              <>
                {data?.['terms-url'] && (
                  <a
                    href={data['terms-url']}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors touch-manipulation ${
                      data?.['privacy-url'] ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-700 font-medium">Termos de Uso</p>
                    </div>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                )}
                {data?.['privacy-url'] && (
                  <a
                    href={data['privacy-url']}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors touch-manipulation"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-700 font-medium">Política de Privacidade</p>
                    </div>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                )}
              </>
            )}
          </div>
        )}

        {/* Version footer */}
        <div className="text-center py-2">
          <p className="text-xs text-gray-400">{VERSION_LABEL}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            &copy; {new Date().getFullYear()} {data?.['app-name'] ?? 'HomeStock AI'}
          </p>
        </div>
      </div>
    </div>
  );
}
